/**
 * The posts on the state exam's writing forms share a shape: what the form is,
 * how often the papers set it, how to build it, phrases, and a sample of ours.
 * The figures are generated here from `BLOG_FACTS.stateFormats` and the
 * regulation's lists of forms, so no post types a count; each post supplies
 * only its own words.
 */
import { PAPER_WRITING_CRITERIA } from "@shared/certyfikat";
import { countWords } from "@shared/writing-review";
import { PAPERS_SOURCE } from "@/features/landing/numbers";
import type { Locale } from "@/i18n";
import { BLOG_FACTS, LEVEL_STANDARDS as LS } from "./facts";
import type { WritingFormat } from "./formats";
import type { Block, PostBody } from "./types";

type Level = "B1" | "B2";
const LEVELS: Level[] = ["B1", "B2"];

/** The forms the regulation lists for the writing part (Dz.U. 2025 poz. 217, zał. 1, cz. II, pkt 4.2). */
export const LISTED_FORMS: Record<Level, number> = { B1: 16, B2: 23 };

export interface FormatText {
  title: string;
  description: string;
  lead: string;
  /** Two points; the third, on the exam, is generated. */
  summary: [string, string];
  /** Optional blocks between the lead and the exam section — what the form is. */
  intro?: Block[];
  structureTitle: string;
  structure: string[];
  phrasesTitle: string;
  phrasesHead: [string, string];
  sampleTitle: string;
  sampleNote: string;
  extra?: Block[];
  faq: { q: string; a: string }[];
  cta: { title: string; text: string };
}

export interface FormatSpec {
  /** The key `writingFormat` gives this form, or a key it never gives (counted as none). */
  form: WritingFormat | "zawiadomienie";
  /** Whether the regulation's list of forms names it at each level. */
  listed: Record<Level, boolean>;
  phrases: string[][];
  /** Our own sample, in Polish; blank lines split paragraphs. */
  sample: string;
  text: Record<Locale, FormatText>;
}

const W: Record<Locale, { kind: Record<"short" | "long" | "both", string>; set: (l: Level, p: number, t: number, kind: string, words: string) => string; none: (l: Level, t: number) => string; unlisted: (l: Level) => string; words: (min: number, max: number) => string; h2: string; how: (b1: string, b2: string) => string; summary: (parts: string[]) => string }> = {
  pl: {
    kind: { short: "jako krótki tekst", long: "jako długi tekst", both: "jako krótki albo długi tekst" },
    set: (l, p, t, kind, words) => `na ${l} — w ${p} z ${t} arkuszy w bibliotece, ${kind}, ${words}`,
    none: (l, t) => `na ${l} — ta forma jest na liście form w rozporządzeniu, ale nie ma jej w żadnym z ${t} arkuszy w bibliotece`,
    unlisted: (l) => `na ${l} — tej formy nie ma na liście form tego poziomu`,
    words: (a, b) => (a === b ? `${a} słów` : `${a}–${b} słów`),
    h2: "Kiedy ta forma jest na egzaminie?",
    how: (b1, b2) => `W części pisemnej egzaminu państwowego piszesz dwa teksty — krótki i długi — z jednego z trzech zestawów do wyboru, więc tej formy nie musisz wybierać. Arkusz drukuje dla całej części trzy kryteria: realizację zadania, zakres środków językowych i poprawność — ${b1} pkt na B1 i ${b2} pkt na B2 za oba teksty razem. Ile z tego przypada na jeden tekst, Komisja nie publikuje. Całą część pisemną opisuje strona [pisanie](/pisanie).`,
    summary: (p) => `Na egzaminie państwowym: ${p.join("; ")}.`,
  },
  uk: {
    kind: { short: "як короткий текст", long: "як довгий текст", both: "як короткий або довгий текст" },
    set: (l, p, t, kind, words) => `на ${l} — у ${p} з ${t} наборів у бібліотеці, ${kind}, ${words}`,
    none: (l, t) => `на ${l} — є в переліку форм у розпорядженні, але немає в жодному з ${t} наборів у бібліотеці`,
    unlisted: (l) => `на ${l} — її немає в переліку форм цього рівня`,
    words: (a, b) => (a === b ? `${a} слів` : `${a}–${b} слів`),
    h2: "Коли ця форма трапляється на іспиті?",
    how: (b1, b2) => `У письмовій частині державного іспиту пишеш два тексти — короткий і довгий — з одного з трьох комплектів на вибір, тож цю форму обирати не обов'язково. Набір друкує для всієї частини три критерії: виконання завдання, діапазон мовних засобів і правильність — ${b1} б. на B1 і ${b2} б. на B2 за обидва тексти разом. Скільки з цього припадає на один текст, Комісія не публікує. Усю письмову частину описує сторінка [письмо](/pisanie).`,
    summary: (p) => `На державному іспиті: ${p.join("; ")}.`,
  },
  ru: {
    kind: { short: "как короткий текст", long: "как длинный текст", both: "как короткий или длинный текст" },
    set: (l, p, t, kind, words) => `на ${l} — в ${p} из ${t} наборов в библиотеке, ${kind}, ${words}`,
    none: (l, t) => `на ${l} — есть в перечне форм в распоряжении, но нет ни в одном из ${t} наборов в библиотеке`,
    unlisted: (l) => `на ${l} — её нет в перечне форм этого уровня`,
    words: (a, b) => (a === b ? `${a} слов` : `${a}–${b} слов`),
    h2: "Когда эта форма встречается на экзамене?",
    how: (b1, b2) => `В письменной части государственного экзамена пишешь два текста — короткий и длинный — из одного из трёх комплектов на выбор, так что эту форму выбирать не обязательно. Набор печатает для всей части три критерия: выполнение задания, диапазон языковых средств и правильность — ${b1} б. на B1 и ${b2} б. на B2 за оба текста вместе. Сколько из этого приходится на один текст, Комиссия не публикует. Всю письменную часть описывает страница [письмо](/pisanie).`,
    summary: (p) => `На государственном экзамене: ${p.join("; ")}.`,
  },
  en: {
    kind: { short: "as the short text", long: "as the long text", both: "as the short or the long text" },
    set: (l, p, t, kind, words) => `at ${l} — on ${p} of the ${t} papers in the library, ${kind}, ${words}`,
    none: (l, t) => `at ${l} — on the regulation's list of forms, but on none of the ${t} papers in the library`,
    unlisted: (l) => `at ${l} — not on that level's list of forms`,
    words: (a, b) => (a === b ? `${a} words` : `${a}–${b} words`),
    h2: "When does this form come up in the exam?",
    how: (b1, b2) => `In the state exam's writing part you write two texts — a short one and a long one — from one of three sets to choose from, so you never have to pick this form. The paper prints three criteria for the whole part: task, range and accuracy — ${b1} points at B1 and ${b2} at B2 for both texts together. How that splits between the texts, the Commission does not publish. The whole writing part is described on [writing](/pisanie).`,
    summary: (p) => `In the state exam: ${p.join("; ")}.`,
  },
};

const total = (l: Level) => {
  const c = PAPER_WRITING_CRITERIA[l];
  return c.task + c.range + c.accuracy;
};

/** One clause per level: how often the papers set the form, or that they do not. */
export function examFacts(spec: Pick<FormatSpec, "form" | "listed">, locale: Locale): string[] {
  const w = W[locale];
  return LEVELS.map((l) => {
    const f = BLOG_FACTS.stateFormats[l];
    const e = (f.forms as Record<string, { papers: number; short: number; long: number; words: { min: number; max: number } }>)[spec.form];
    if (!e) return spec.listed[l] ? w.none(l, f.papers) : w.unlisted(l);
    const kind = e.short && e.long ? w.kind.both : e.short ? w.kind.short : w.kind.long;
    return w.set(l, e.papers, f.papers, kind, w.words(e.words.min, e.words.max));
  });
}

const SOURCES = [
  { label: "Państwowa Komisja — testy z poprzednich lat (arkusze B1 i B2)", url: PAPERS_SOURCE },
  { label: "Rozporządzenie z 14 lutego 2025 r. (Dz.U. 2025 poz. 217), załącznik nr 1, część II, pkt 4.2 — formy wypowiedzi pisemnej na B1 i B2", url: LS.source },
];

const NOUN: Record<Locale, Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }> = {
  pl: { one: "słowo", few: "słowa", many: "słów", other: "słowa" },
  uk: { one: "слово", few: "слова", many: "слів", other: "слова" },
  ru: { one: "слово", few: "слова", many: "слов", other: "слова" },
  en: { one: "word", other: "words" },
};
/** "163 słowa", "150 słów" — a count of words in the reader's language, declined. */
export const wordCount = (n: number, l: Locale) => `${n} ${NOUN[l][new Intl.PluralRules(l).select(n)] ?? NOUN[l].other}`;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Every spec built, for `tests/blog.test.ts` to hold each sample to the lengths the papers print. */
export const FORMAT_SPECS: FormatSpec[] = [];

export function formatPost(spec: FormatSpec): Record<Locale, PostBody> {
  FORMAT_SPECS.push(spec);
  const words = countWords(spec.sample);
  const paragraphs = spec.sample.split(/\n+/).map((text) => ({ kind: "p" as const, text }));
  const build = (l: Locale): PostBody => {
    const t = spec.text[l];
    const facts = examFacts(spec, l);
    return {
      title: t.title,
      description: t.description,
      lead: t.lead,
      summary: [...t.summary, W[l].summary(facts)],
      blocks: [
        ...(t.intro ?? []),
        { kind: "h2", id: "egzamin", text: W[l].h2 },
        { kind: "list", items: facts.map(cap) },
        { kind: "p", text: W[l].how(String(total("B1")), String(total("B2"))) },
        { kind: "h2", id: "uklad", text: t.structureTitle },
        { kind: "list", ordered: true, items: t.structure },
        { kind: "h2", id: "zwroty", text: t.phrasesTitle },
        { kind: "table", head: t.phrasesHead, rows: spec.phrases },
        { kind: "h2", id: "wzor", text: t.sampleTitle.replace("{words}", wordCount(words, l)) },
        ...paragraphs,
        { kind: "p", text: t.sampleNote },
        ...(t.extra ?? []),
      ],
      faq: t.faq,
      sources: SOURCES,
      cta: t.cta,
    };
  };
  return { pl: build("pl"), uk: build("uk"), ru: build("ru"), en: build("en") };
}
